/**
 * Dot-matrix chevron arrow used as carousel / slider nav.
 *
 * Pair with a button that owns the `group` class so the `group-hover:animate-pixel-pulse`
 * animation triggers a wave across the dots on hover.
 *
 * Reusable primitive shared by the public templates carousel, the dashboard
 * Featured on B3OS row, and any future "previous/next" control.
 */

const PIXEL_ROWS = 7;
const PIXEL_COLS = 4;
const PIXEL_CELL_PX = 2;
const PIXEL_GAP_PX = 1;
const PIXEL_DELAY_MS = 70;

type LitPixel = readonly [row: number, col: number, delayStep: number];

const LEFT_PIXELS: readonly LitPixel[] = [
  [0, 3, 3],
  [1, 2, 2],
  [2, 1, 1],
  [3, 0, 0],
  [4, 1, 1],
  [5, 2, 2],
  [6, 3, 3],
];

const RIGHT_PIXELS: readonly LitPixel[] = [
  [0, 0, 3],
  [1, 1, 2],
  [2, 2, 1],
  [3, 3, 0],
  [4, 2, 1],
  [5, 1, 2],
  [6, 0, 3],
];

export function PixelArrow({ direction }: { direction: "left" | "right" }) {
  const pixels = direction === "left" ? LEFT_PIXELS : RIGHT_PIXELS;

  return (
    <div
      aria-hidden
      className="text-ds-text-subtle grid"
      style={{
        gridTemplateColumns: `repeat(${PIXEL_COLS}, ${PIXEL_CELL_PX}px)`,
        gridTemplateRows: `repeat(${PIXEL_ROWS}, ${PIXEL_CELL_PX}px)`,
        gap: `${PIXEL_GAP_PX}px`,
      }}
    >
      {pixels.map(([r, c, step]) => (
        <span
          key={`${r}-${c}`}
          className="group-hover:animate-pixel-pulse block h-full w-full bg-current"
          style={{
            gridRow: r + 1,
            gridColumn: c + 1,
            animationDelay: `${step * PIXEL_DELAY_MS}ms`,
          }}
        />
      ))}
    </div>
  );
}
